import React from 'react';

import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import CollapsableContent from 'src/_components/CollapsableContent';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function GettingStarted() {
  return (
    <>
      <Meta
        title="Getting Started with the Vimeo API"
        description="Learn how to work with representations, headers, parameters, and batch requests in the Vimeo API."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Link href={'/api/guides/start'}>Getting Started</Path.Link>
            <Path.Current>The Basics</Path.Current>
          </Path>
        }
        nav={{ page: 'getting-started', subpage: 'getting-started' }}
      >
        <Header>The Basics</Header>

        <Paragraph size="2">Newbie? Welcome!</Paragraph>
        <Paragraph size="2">
          We've put together this series of Getting Started guides to help you learn the Vimeo API.
          Fully functional scripts in multiple languages await you.
        </Paragraph>

        <Paragraph size="2">
          If you've worked with APIs in general before, or if you've already dipped your toe into
          ours, you might want to skip to the guide of your choice. You can explore them in any
          order. They're like a choose-your-own-adventure story, only every branch leads to the
          best-case ending.
        </Paragraph>

        <Paragraph size="2">
          But if you need some help getting started with...ah...getting started, you're absolutely
          on the right page. We'll step you through exactly what you need to do to develop for the
          API, and we'll make sure you look good doing it.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> The Vimeo API is a REST API. REST, or{' '}
          <em>representational state transfer,</em> is an architectural style or design philosophy
          for interacting with online resources (like videos) using standard HTTP methods, including
          GET, POST, and PATCH. We built the Vimeo API to adhere to REST principles and practices.
          For more information about REST, see RESTfulAPI.net's{' '}
          <Link variant="minimal" href="https://restfulapi.net/">
            REST API Tutorial
          </Link>
          .
        </StyledNotice>

        <Header size="2" id="using-the-examples">
          Using the examples
        </Header>
        <Paragraph size="2">
          The example scripts that we've provided here are complete and ready for you to use,
          although most of them require a little customization. Placeholders for values that are
          specific to you appear in the code between braces, like this:
        </Paragraph>

        <CodeBlock language="php">{`$client = new Vimeo("{client_id}", "{client_secret}", "{access_token}");`}</CodeBlock>

        <Paragraph size="2">
          The braces are part of the placeholder name, so, assuming that your client identifier is{' '}
          <code>123</code> (it isn't), your client secret is <code>abc</code> (it isn't), and your
          access token is <code>a1b</code> (it isn't), you'd customize the code as follows, without
          the braces:
        </Paragraph>

        <CodeBlock language="php">{`$client = new Vimeo("123", "abc", "a1b");`}</CodeBlock>

        <StyledNotice>
          <strong>NOTE:</strong> If you aren't clear about what these placeholders represent, we'll
          explain that, too, as we go along.
        </StyledNotice>

        <Paragraph size="2">
          This placeholder format appears across the developer site, so don't be surprised if you
          see it elsewhere.
        </Paragraph>

        <Paragraph size="2">
          Now, about those examples: we'll get to them shortly. First, let's cover the
          preliminaries.
        </Paragraph>

        <Header size="2" id="register-your-app">
          Register your app
        </Header>
        <Paragraph size="2">
          Before we set you loose on the API, we ask that you provide a little information about
          your app. An <em>app</em> in this sense can be a full-featured mobile application, a
          dynamic web page, or a three-line script. If it's making API calls, it's an app.
        </Paragraph>

        <Paragraph size="2">
          Maybe you don't have an app to register. Or maybe your app is still early in development.
          If this is you, that's more than fine. You can make up an app to use with these guides.
        </Paragraph>

        <Paragraph size="2">To register your app:</Paragraph>
        <ol>
          <li>
            Go to the{' '}
            <Link variant="minimal" href="/apps">
              My Apps
            </Link>{' '}
            page.
          </li>
          <li>
            Click <strong>Create an app</strong>. Your browser goes to the Create a New App page.
          </li>
          <li>
            In the{' '}
            <code>
              <b>App name</b>
            </code>{' '}
            field, type the name of your app.
          </li>
          <li>
            In the{' '}
            <code>
              <b>App description</b>
            </code>{' '}
            field, type a brief description of your app. This text appears in the authorization
            prompt, which your end users receive when your app requests permission to access their
            Vimeo account.
          </li>
          <li>
            Set the access permissions to your app under{' '}
            <strong>Will people besides you be able to access your app?</strong> by selecting either{' '}
            <code>No</code> (only you can access your app) or <code>Yes</code> (other Vimeo accounts
            can access your app).
          </li>
          <li>
            At the bottom of the form, check the confirmation checkbox to agree to the{' '}
            <Link variant="minimal" href="https://developer.vimeo.com/guidelines/terms">
              Vimeo API License Addendum
            </Link>{' '}
            and the{' '}
            <Link variant="minimal" href="https://vimeo.com/terms">
              Vimeo Terms of Service
            </Link>
            .
          </li>
          <li>
            Click <strong>Create App</strong>.
          </li>
        </ol>

        <Header size="2" id="setup-vimeo-sdk">
          Set up a Vimeo SDK
        </Header>
        <Paragraph size="2">
          Next, you'll want to set up your development environment to connect and interact with the
          Vimeo API. Our SDKs have you covered for that.
        </Paragraph>

        <Paragraph size="2">
          We offer official support for about a dozen environments (and a couple others on the sly);
          see our{' '}
          <Link variant="minimal" href="/api/libraries">
            Libraries and SDKs
          </Link>{' '}
          page for the complete list. But for the purpose of these Getting Started guides, we
          suggest that you work in PHP, Python, or Node.js. We've written our examples specifically
          for these languages, so you'll have an easier time following along.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> If you'd like examples in different languages,{' '}
          <Link variant="minimal" href={devsite.vimeo_url + '/help/contact'}>
            let us know
          </Link>
          , and we'll see what we can do.
        </StyledNotice>

        <Paragraph size="2">
          To get step-by-step instructions for setting up the Vimeo SDK of your choice, look under
          the following expandable headings.
        </Paragraph>

        <CollapsableContent element={'h3'} label="PHP" style={{ marginBottom: '1rem' }}>
          <Paragraph size="2">To set up the Vimeo SDK for PHP:</Paragraph>

          <ol>
            <li>
              If necessary, install{' '}
              <Link variant="minimal" href="https://getcomposer.org">
                Composer
              </Link>
              .
            </li>
            <li>Open your computer's command line interface.</li>
            <li>Navigate to the root folder of your project.</li>
            <li>Enter the following command:</li>
          </ol>

          <CodeBlock language="bash">composer require vimeo/vimeo-api</CodeBlock>
        </CollapsableContent>

        <CollapsableContent element={'h3'} label="Python" style={{ marginBottom: '1rem' }}>
          <Paragraph size="2">To set up the Vimeo SDK for Python:</Paragraph>

          <ol>
            <li>
              If necessary, go to the{' '}
              <Link variant="minimal" href="https://pypi.org">
                PyPI
              </Link>{' '}
              site to install pip, although you probably already have it from your Python
              installation.
            </li>
            <li>Open your computer's command line interface.</li>
            <li>Navigate to the root folder of your project.</li>
            <li>Enter the following command:</li>
          </ol>

          <CodeBlock language="bash">pip install PyVimeo</CodeBlock>
        </CollapsableContent>

        <CollapsableContent element={'h3'} label="Node.js" style={{ marginBottom: '1rem' }}>
          <Paragraph size="2">To set up the Vimeo SDK for Node.js:</Paragraph>

          <ol>
            <li>
              If necessary, learn about{' '}
              <Link variant="minimal" href="https://www.npmjs.com">
                npm
              </Link>
              , which comes with your Node.js installation.
            </li>
            <li>Open your computer's command line interface.</li>
            <li>Navigate to the root folder of your project.</li>
            <li>Enter the following command:</li>
          </ol>

          <CodeBlock language="bash">npm install vimeo</CodeBlock>
        </CollapsableContent>

        <Header size="2" id="generate-access-token">
          Generate an access token
        </Header>
        <Paragraph size="2">
          An <em>access token</em> enables your app to make API requests. The token itself is just a
          string of characters that you present to the API like a password. But it also performs
          some other important functions. First, it identifies your app and maybe also your end
          user, depending on its type and how you define its properties. Second, it determines the
          range of permissible actions, or <em>scopes</em>, that your app can perform.
        </Paragraph>

        <Paragraph size="2">
          You create the token through the API or directly from the{' '}
          <Link variant="minimal" href="/apps">
            My Apps
          </Link>{' '}
          page; we explain it all in our{' '}
          <Link variant="minimal" href="/api/authentication">
            authentication documentation
          </Link>
          , but you can save these details for a later time. All you really need to do for the
          purpose of these guides is to generate a <em>personal access token,</em> which gives you
          API access to your own data.
        </Paragraph>

        <Paragraph size="2">To generate a personal access token:</Paragraph>
        <ol>
          <li>
            Go to the{' '}
            <Link variant="minimal" href="/apps">
              My Apps
            </Link>{' '}
            page.
          </li>
          <li>Click the name of your app. Your browser goes to this app's information page.</li>
          <li>
            In the navigation list on the left side of the page, click{' '}
            <strong>Generate an access token</strong>, or just scroll down the page to this section.
          </li>
          <li>
            If you want to be able to access your private data, select the{' '}
            <strong>Authenticated (you)</strong> option. This generates the Scopes section, which
            offers various scopes. You must check the <code>private</code> scope to proceed. If you
            don't select a token type, your token defaults to Unauthenticated, which is public.
          </li>
          <li>
            Under the Scopes heading, check the scopes that you'd like to give to your app. You need{' '}
            <code>upload</code> for the{' '}
            <Link variant="minimal" href="/api/guides/videos/upload">
              Uploading Videos
            </Link>{' '}
            guide and <code>edit</code> for the{' '}
            <Link variant="minimal" href="/api/guides/videos/interact">
              Interacting with Videos
            </Link>{' '}
            guide. To learn more about scopes, see our{' '}
            <Link variant="minimal" href="/api/authentication">
              authentication documentation
            </Link>
            .
          </li>
          <li>
            Click <strong>Generate</strong>. The new token appears in the Personal Access Tokens
            table.
          </li>
          <li>Copy the token string, and store it in a safe place.</li>

          <StyledNotice>
            <strong>NOTE:</strong> You can't recover a lost token string, but you can always get a
            new one by creating a new token.
          </StyledNotice>
        </ol>

        <Header size="2" id="first-api-call">
          Make your first Vimeo API call
        </Header>
        <Paragraph size="2">
          All that's left to do is to test out your system, just to make sure that everything is
          configured correctly. We've set up an endpoint for exactly this purpose. To call it,
          execute one of the scripts at the bottom of this section, whichever script corresponds to
          the Vimeo SDK that you set up. If you haven't set up an SDK yet, go back to the{' '}
          <Link variant="minimal" href="#setup-vimeo-sdk">
            Set up a Vimeo SDK
          </Link>{' '}
          step and get busy.
        </Paragraph>

        <Paragraph size="2">
          For the PHP, Python, and Node.js scripts, you need to supply your client identifier,
          client secret, and access token wherever you see the{' '}
          <code>
            <b>{`client_id`}</b>
          </code>
          ,{' '}
          <code>
            <b>{`client_secret`}</b>
          </code>
          , and{' '}
          <code>
            <b>{`access_token`}</b>
          </code>{' '}
          placeholders. As for the values: you've already generated an access token, unless you
          skipped the{' '}
          <Link variant="minimal" href="#generate-access-token">
            Generate an access token
          </Link>{' '}
          step, in which case you need to remedy that. The client identifier and client secret come
          from the details page of your app, which opens from{' '}
          <Link variant="minimal" href="/apps">
            My Apps
          </Link>{' '}
          when you click your app's name.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> If you're working in PHP, there's an additional placeholder value:{' '}
          <code>
            <b>{`path_to_root_folder`}</b>
          </code>
          . Fill in this placeholder with the path to the SDK installation on your computer, so that
          you're pointing to the <code>autoload.php</code> file. None of the other scripts require
          this value.
        </StyledNotice>

        <Paragraph size="2">
          We've also included the HTTP code in cURL and as HTTP if you'd rather map the HTTP
          commands to your own request library instead of using our SDKs. You don't need your client
          identifier or client secret for this sort of operation, but you do need to supply your
          access token. It belongs in the{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          header, after the keyword <code>bearer</code> and a space, like this:{' '}
          <code>{'bearer {access_token}'}</code>.
        </Paragraph>

        <div>
          <CollapsableContent
            element={'h3'}
            id="php-example"
            label="PHP"
            style={{ marginBottom: '1rem' }}
          >
            <CodeBlock language="php">{`<?php
  require '{path_to_root_folder}/autoload.php';
  use Vimeo\\Vimeo;

  $client = new Vimeo("{client_id}", "{client_secret}", "{access_token}");

  $response = $client->request('/tutorial', array(), 'GET');
  print_r($response);`}</CodeBlock>
          </CollapsableContent>

          <CollapsableContent
            element={'h3'}
            id="python-example"
            label="Python"
            style={{ marginBottom: '1rem' }}
          >
            <CodeBlock language="python">{`import vimeo

  client = vimeo.VimeoClient(
    token='{access_token}',
    key='{client_id}',
    secret='{client_secret}'
  )

  response = client.get(uri)
  print(response.json())`}</CodeBlock>
          </CollapsableContent>

          <CollapsableContent
            element={'h3'}
            id="node-example"
            label="Node.js"
            style={{ marginBottom: '1rem' }}
          >
            <CodeBlock language="javascript">{`let Vimeo = require('vimeo').Vimeo;
  let client = new Vimeo("{client_id}", "{client_secret}", "{access_token}");

  client.request({
    method: 'GET',
    path: '/tutorial'
  }, function (error, body, status_code, headers) {
    if (error) {
      console.log(error);
    }

    console.log(body);
  })`}</CodeBlock>
          </CollapsableContent>

          <CollapsableContent
            element={'h3'}
            id="http-example"
            label="HTTP"
            style={{ marginBottom: '1rem' }}
          >
            <Paragraph size="2">In cURL:</Paragraph>
            <CodeBlock language="bash">{`curl https://api.vimeo.com/tutorial -H "Authorization: bearer {access_token}"`}</CodeBlock>

            <Paragraph size="2">As HTTP:</Paragraph>
            <CodeBlock language="http">{`GET /tutorial HTTP/1.1
  Host: api.vimeo.com
  Authorization: bearer {access_token}`}</CodeBlock>
          </CollapsableContent>
        </div>

        <Header size="2" id="next-steps">
          Handling errors
        </Header>
        <Paragraph size="2">
          If the script doesn't work, read the error response carefully, and make sure to
          distinguish between the{' '}
          <code>
            <b>user_message</b>
          </code>{' '}
          and{' '}
          <code>
            <b>developer_message</b>
          </code>{' '}
          text. The user message is the general error text, which we've written for a nontechnical
          audience. But the developer message is specifically for developers — it often contains
          more precise and actionable information for debugging.
        </Paragraph>

        <Paragraph size="2">
          You should also make sure that you've supplied your client identifier, client secret, and
          access token string exactly as they appear. If you have, you can generate a new access
          token, and try that one instead.
        </Paragraph>

        <Paragraph size="2">
          If all else fails,{' '}
          <Link variant="minimal" href="https://vimeo.com/help/contact">
            contact us
          </Link>
          . Please include your entire client identifier (but not your client secret); the first
          seven digits of your access token; the HTTP method and URL of your API call (which in this
          case is a GET to <code>/tutorial</code>); and the full response that you received from the
          API.
        </Paragraph>

        <Header size="2" id="next-steps">
          What's next?
        </Header>
        <Paragraph size="2">
          All set up? On to the guides! In{' '}
          <Link variant="minimal" href="/api/guides/videos/upload">
            Uploading Videos
          </Link>
          , we show you how to upload new videos to Vimeo. This is where most devs will start. But
          if modifying existing videos is more your thing, start with{' '}
          <Link variant="minimal" href="/api/guides/videos/interact">
            Interacting with Videos
          </Link>{' '}
          instead.
        </Paragraph>

        <Paragraph size="2">
          And if you need help, don't suffer in silence.{' '}
          <Link variant="minimal" href={devsite.vimeo_url + '/help/contact'}>
            Contact us
          </Link>{' '}
          and let us prove that we love you.
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
